/**
 * What kind of file a contract template was uploaded as, and what the file input
 * should offer. ONE home: the library upload (PracticeTemplatesScreen) and the
 * one-off send (EnquiryDetailScreen) must agree, or the same file is accepted by
 * one route and refused by the other.
 *
 * Every accepted file — text or document — is stored and served exactly as
 * written (`getContractAgreementFile`); a text upload containing a {{...}} token
 * is refused (v7.192). Every signed contract also carries a generated Schedule
 * of Particulars naming the client.
 *
 * The server refuses anything outside CONTRACT_FILE_ACCEPT — src/lib/contract-template-file.ts
 * is the authority, and the two are pinned together by an architecture gate.
 */

/** Extensions that are never a text body, whatever MIME the browser reports. */
const DOCUMENT_EXTENSION = /\.(pdf|docx?|rtf|odt|pages|zip)$/i;
const TEXT_EXTENSION = /\.(txt|md|markdown)$/i;

/**
 * Whether the file is a text contract body.
 *
 * ORDER IS THE WHOLE POINT. Extensions are decided first, because browsers report
 * an EMPTY `type` for plenty of files — including .docx and .pdf on some platforms
 * — and an empty type alone used to mean "text". A .docx that arrived with no MIME
 * was therefore UTF-8-decoded, and the resulting mojibake became the agreement the
 * client was asked to sign. Only once neither extension decides it does the MIME
 * type, then the empty-type fallback (a .md file the browser did not recognise),
 * get a say. `accept` is a picker hint a user can bypass, so this must hold for a
 * file of any name.
 */
export function isTextContractFile(file: File): boolean {
  if (DOCUMENT_EXTENSION.test(file.name)) return false;
  if (TEXT_EXTENSION.test(file.name)) return true;
  return file.type.startsWith('text/') || file.type === '';
}

/** Strip the text-contract extension to suggest a template name. */
export function contractNameFromFile(file: File): string {
  return file.name.replace(TEXT_EXTENSION, '') || 'One-off agreement';
}

/**
 * Offered in the file picker. A user may still choose "All files" and pick
 * anything, which is why the server re-checks every upload.
 */
export const CONTRACT_FILE_ACCEPT =
  '.txt,.md,.markdown,.pdf,text/plain,text/markdown,application/pdf';

/** Shown once a contract file is selected, whatever its type. */
export const CONTRACT_DOCUMENT_NOTE =
  'Uploaded exactly as written. A signed agreement also carries a generated '
  + 'Schedule of Particulars naming the client, bound to the document.';

/** Stated before the picker opens, so a Word user learns the rule without a refusal. */
export const CONTRACT_FILE_HINT =
  'Accepted: PDF (.pdf) or a text file (.txt, .md, .markdown). Word documents are not '
  + 'accepted. Use File → Save as / Export → PDF and upload the PDF.';
